/**
 * Infinite Scroll for collection grids and featured collection sections.
 * Supports legacy collection pages plus scoped page-section containers.
 */

class CollectionInfiniteScroll {
  constructor(container) {
    this.container = container;
    this.loader = this.findLoader();
    this.endMessage = this.findEndMessage();
    this.grid = this.findGrid();
    this.containerId = this.container.dataset.infiniteScrollId || '';

    const mobileBp = (window.theme && window.theme.config && window.theme.config.bpSmallValue) || 768;
    this.mediaQuery = window.matchMedia(`(max-width: ${mobileBp}px)`);

    this.observer = null;
    this.isFetching = false;

    if (!this.container || !this.loader || !this.grid) {
      this.isReady = false;
      return;
    }

    this.isReady = true;
    this.enableDesktop = this.container.dataset.infiniteDesktop === 'true';
    this.enableMobile = this.container.dataset.infiniteMobile === 'true';

    this.init();
  }

  init() {
    this.checkActivation();

    const handleBreakpointChange = () => {
      this.checkActivation();
    };

    if (this.mediaQuery.addEventListener) {
      this.mediaQuery.addEventListener('change', handleBreakpointChange);
    } else if (this.mediaQuery.addListener) {
      this.mediaQuery.addListener(handleBreakpointChange);
    }
  }

  refresh() {
    this.loader = this.findLoader();
    this.endMessage = this.findEndMessage();
    this.grid = this.findGrid();
    this.enableDesktop = this.container.dataset.infiniteDesktop === 'true';
    this.enableMobile = this.container.dataset.infiniteMobile === 'true';

    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    if (!this.loader || !this.grid) return;
    this.checkActivation();
  }

  findLoader() {
    return this.container.querySelector('[data-infinite-scroll-loader], #InfiniteScrollLoader');
  }

  findEndMessage() {
    return this.container.querySelector('[data-infinite-scroll-end], #InfiniteScrollEnd');
  }

  findGrid() {
    return this.container.querySelector('[data-infinite-scroll-grid], .grid--uniform');
  }

  getNextLink() {
    return this.container.querySelector('.infinite-scroll-next');
  }

  checkActivation() {
    const isMobile = this.mediaQuery.matches;
    const isActive = (isMobile && this.enableMobile) || (!isMobile && this.enableDesktop);

    if (isActive) {
      this.container.classList.add('infinite-scroll-active');
      this.loader.style.display = '';
      if (this.endMessage) this.endMessage.style.display = '';
      if (this.endMessage && this.getNextLink()) {
        this.endMessage.classList.add('loading-hidden');
      }
      this.setupObserver();

      if (!this.getNextLink()) {
        this.showEndMessage();
      }
    } else {
      this.container.classList.remove('infinite-scroll-active');
      this.loader.style.display = 'none';
      if (this.endMessage) this.endMessage.style.display = 'none';
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }
  }

  setupObserver() {
    this.loader = this.findLoader();
    const nextLink = this.getNextLink();
    if (!nextLink || !this.loader) return;

    if (this.observer) this.observer.disconnect();

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !this.isFetching) {
          const currentNextLink = this.getNextLink();
          if (currentNextLink) {
            this.fetchNextPage(currentNextLink.href);
          } else if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
          }
        }
      });
    }, {
      rootMargin: '400px'
    });

    this.observer.observe(this.loader);
  }

  async fetchNextPage(url) {
    this.isFetching = true;
    this.showLoader();

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Network response was not ok');

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const sourceContainer = this.findSourceContainer(doc);

      if (!sourceContainer) {
        this.showEndMessage();
        return;
      }

      const sourceGrid = sourceContainer.querySelector('[data-infinite-scroll-grid], .grid--uniform');
      const newGridItems = this.getDirectGridItems(sourceGrid);
      const newPagination = sourceContainer.querySelector('.pagination');

      if (!this.grid || newGridItems.length === 0) {
        this.showEndMessage();
        return;
      }

      console.log('[InfiniteScroll] Appending', newGridItems.length, 'grid items');

      const appendedItems = [];

      newGridItems.forEach((item) => {
        this.unveilImages(item);
        this.activateAos(item);
        this.grid.appendChild(item);
        appendedItems.push(item);
      });

      if (typeof AOS !== 'undefined' && AOS.refresh) {
        AOS.refresh();
      }

      this.renderKlaviyoRatings(appendedItems);

      if (this.shouldUpdateHistory()) {
        history.replaceState(null, null, url);
      }

      this.replacePagination(newPagination);

      if (newPagination && newPagination.querySelector('.infinite-scroll-next')) {
        this.setupObserver();
      } else {
        this.showEndMessage();
      }
    } catch (error) {
      console.error('Infinite scroll fetch failed:', error);
    } finally {
      this.isFetching = false;
      this.hideLoader();
    }
  }

  findSourceContainer(doc) {
    if (this.containerId) {
      const scopedContainers = doc.querySelectorAll('[data-infinite-scroll-container]');
      return Array.from(scopedContainers).find((container) => {
        return container.dataset.infiniteScrollId === this.containerId;
      });
    }

    return doc.querySelector('#CollectionAjaxContent');
  }

  getDirectGridItems(grid) {
    if (!grid) return [];

    return Array.from(grid.children).filter((child) => {
      return child.classList && child.classList.contains('grid__item');
    });
  }

  unveilImages(item) {
    item.querySelectorAll('img').forEach((img) => {
      const dataSrc = img.getAttribute('data-src');
      if (dataSrc) {
        img.setAttribute('src', dataSrc.replace(/\{width\}/g, '720'));
        img.removeAttribute('data-src');
      }

      const dataSrcset = img.getAttribute('data-srcset');
      if (dataSrcset) {
        img.setAttribute('srcset', dataSrcset);
        img.removeAttribute('data-srcset');
      }

      img.removeAttribute('data-widths');
      img.removeAttribute('data-sizes');
      img.classList.remove('lazyload');
      img.classList.add('lazyloaded');
    });
  }

  activateAos(item) {
    item.querySelectorAll('[data-aos]').forEach((el) => {
      el.classList.add('aos-init', 'aos-animate');
    });

    if (item.hasAttribute('data-aos')) {
      item.classList.add('aos-init', 'aos-animate');
    }
  }

  renderKlaviyoRatings(items) {
    if (!window.theme || typeof window.theme.renderKlaviyoStarRatings !== 'function') return;

    (items || []).forEach((item) => {
      window.theme.renderKlaviyoStarRatings(item);
    });
  }

  replacePagination(newPagination) {
    const currentPagination = this.container.querySelector('.pagination');
    if (currentPagination) currentPagination.remove();
    if (!newPagination) return;

    const anchor = this.loader || this.endMessage;
    if (anchor && anchor.parentNode === this.container) {
      this.container.insertBefore(newPagination, anchor);
    } else {
      this.container.appendChild(newPagination);
    }
  }

  shouldUpdateHistory() {
    if (this.container.dataset.infiniteScrollHistory === 'false') return false;
    return document.body && document.body.classList.contains('template-collection');
  }

  showLoader() {
    if (this.loader) this.loader.classList.remove('loading-hidden');
  }

  hideLoader() {
    if (this.loader) this.loader.classList.add('loading-hidden');
  }

  showEndMessage() {
    if (this.endMessage) this.endMessage.classList.remove('loading-hidden');
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }
}

const initInfiniteScroll = () => {
  const containers = Array.from(document.querySelectorAll('[data-infinite-scroll-container]'));
  const legacyContainer = document.querySelector('#CollectionAjaxContent');

  if (legacyContainer && !containers.includes(legacyContainer)) {
    containers.push(legacyContainer);
  }

  containers.forEach((container) => {
    if (container.collectionInfiniteScroll) {
      container.collectionInfiniteScroll.refresh();
      return;
    }

    const instance = new CollectionInfiniteScroll(container);
    if (instance.isReady) {
      container.collectionInfiniteScroll = instance;
    }
  });
};

document.addEventListener('DOMContentLoaded', initInfiniteScroll);
document.addEventListener('page:loaded', initInfiniteScroll);
document.addEventListener('ajaxProductGridUpdate', initInfiniteScroll);
document.addEventListener('collection:reloaded', initInfiniteScroll);
